import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { v4 as uuidv4 } from 'uuid';

import {
  IRawStreamChunk,
  IResponseBlock,
  IStreamConversation,
  ISendInput,
  IUseStreamingConversationConfig,
  IUseStreamingConversationResult,
  TStreamEvent,
  TStreamingState,
} from './types';

/**
 * 将 ERROR 类型的事件归一化为标准的 IResponseBlock 错误块。
 * 用于在 answer 列表中展示错误信息。
 */
const normalizeErrorBlock = (event: Extract<TStreamEvent, { kind: 'ERROR' }>): IResponseBlock => {
  const fallbackMessage = event.message || 'Streaming request failed.';
  return {
    type: 'error',
    value: fallbackMessage,
    status: 'error',
    meta: {
      error: event.error,
    },
  };
};

/**
 * 核心 reducer：将单个流事件应用到当前 answer 块列表，返回新的 answer 与派生状态。
 * 支持的事件类型：TEXT_DELTA（文本增量）、APPEND_BLOCK（追加块）、ERROR、DONE、SET_STATE。
 */
const applyEventToAnswer = (
  answer: IResponseBlock[],
  event: TStreamEvent,
): {
  nextAnswer: IResponseBlock[];
  nextState?: Exclude<TStreamingState, 'idle'>;
} => {
  if (event.kind === 'TEXT_DELTA') {
    const deltaText = String(event.text || '');
    const normalized = deltaText
      .replace(/\s+/g, '')
      .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '');
    // 心跳帧/空白帧/不可见字符帧不应触发状态推进与渲染
    if (!normalized) {
      return { nextAnswer: answer };
    }
    const nextAnswer = [...answer];
    const lastBlock = nextAnswer[nextAnswer.length - 1];
    if (lastBlock?.type === 'text' && typeof lastBlock.value === 'string') {
      // 创建新对象替换，保证不可变性
      nextAnswer[nextAnswer.length - 1] = {
        ...lastBlock,
        value: lastBlock.value + deltaText,
      };
      return { nextAnswer, nextState: 'progressing' };
    }
    nextAnswer.push({
      type: 'text',
      value: deltaText,
      status: 'pending',
    });
    return { nextAnswer, nextState: 'progressing' };
  }

  if (event.kind === 'APPEND_BLOCK') {
    return {
      nextAnswer: [...answer, event.block],
      nextState: 'progressing',
    };
  }

  if (event.kind === 'ERROR') {
    return {
      nextAnswer: [...answer, normalizeErrorBlock(event)],
      nextState: 'rejected',
    };
  }

  if (event.kind === 'DONE') {
    const nextAnswer: IResponseBlock[] = answer.map((item) => {
      if (item.status === 'pending') {
        return {
          ...item,
          status: 'success',
        };
      }
      return item;
    });
    return { nextAnswer, nextState: 'complete' };
  }

  if (event.kind === 'SET_STATE') {
    return {
      nextAnswer: answer,
      nextState: event.state,
    };
  }

  return { nextAnswer: answer };
};

/**
 * 通用流式会话 Hook。
 * - 传输层固定为 SSE（fetchEventSource）
 * - 协议差异通过 parseChunk 抽象
 * - 状态归并统一在内部 reducer 处理
 */
export const useStreamingConversation = <TInput = unknown>(
  config: IUseStreamingConversationConfig<TInput>,
): IUseStreamingConversationResult<TInput> => {
  const {
    buildRequest,
    parseChunk,
    createMessageId,
    getInitialAnswer,
    onBeforeSend,
    onOpen,
    onSuccessChunk,
    onComplete,
    onError,
  } = config;

  // ---- React 状态 ----
  // conversations: 外部读取用的会话列表（经 RAF 批量刷新）
  const [conversations, setConversationsState] = useState<IStreamConversation<TInput>[]>([]);
  // streamingState: 当前流式状态机（idle/pending/progressing/complete/rejected）
  const [streamingState, setStreamingState] = useState<TStreamingState>('idle');

  // ---- Refs（用于高频/异步操作，避免闭包过期与重复渲染）----
  // 当前正在进行的请求 AbortController，用于手动取消或覆盖发送
  const abortControllerRef = useRef<AbortController | null>(null);
  // 最近一次已发布的 conversations（供 updater 函数读取前值）
  const conversationsRef = useRef<IStreamConversation<TInput>[]>([]);
  // 待通过 RAF 发布到 React 状态的“暂存”conversations
  const stagedConversationsRef = useRef<IStreamConversation<TInput>[] | null>(null);
  // RAF 句柄，用于去重与清理
  const publishRafIdRef = useRef<number | null>(null);
  // 按 messageId 聚合的待刷新文本增量（缓冲高频 TEXT_DELTA，减少 setState 次数）
  const pendingTextDeltaByMessageIdRef = useRef<Record<string, string>>({});
  // 文本增量 flush 定时器句柄
  const textDeltaFlushTimerRef = useRef<number | null>(null);

  // ---- 常量：文本刷新策略 ----
  // 文本增量定时 flush 间隔（ms），兼顾响应速度与渲染性能
  const TEXT_DELTA_FLUSH_MS = 80;
  // 文本增量长度阈值：超过该值立即 flush，避免大段文本延迟显示
  const TEXT_DELTA_FLUSH_CHAR_THRESHOLD = 64;

  /**
   * 立即将 stagedConversations 同步发布到 React State。
   * 会取消待执行的 RAF，确保数据不丢失（用于发送前、取消、close/error 等边界）。
   */
  const publishNow = useCallback(() => {
    if (publishRafIdRef.current !== null) {
      window.cancelAnimationFrame(publishRafIdRef.current);
      publishRafIdRef.current = null;
    }
    if (!stagedConversationsRef.current) {
      return;
    }
    const next = stagedConversationsRef.current;
    stagedConversationsRef.current = null;
    conversationsRef.current = next;
    setConversationsState(next);
  }, []);

  /**
   * 通过 requestAnimationFrame 批量发布 stagedConversations。
   * 同一帧内多次调用只会产生一次 setState，减少重渲染。
   */
  const schedulePublish = useCallback(() => {
    if (publishRafIdRef.current !== null) {
      return;
    }
    publishRafIdRef.current = window.requestAnimationFrame(() => {
      publishRafIdRef.current = null;
      if (!stagedConversationsRef.current) {
        return;
      }
      const next = stagedConversationsRef.current;
      stagedConversationsRef.current = null;
      conversationsRef.current = next;
      setConversationsState(next);
    });
  }, []);

  /**
   * 内部更新 conversations：写入 stagedConversationsRef 并调度 RAF 发布。
   * 支持直接赋值或函数式 updater，保证高频更新不触发频繁重渲染。
   */
  const setConversationsInternal = useCallback(
    (
      updater:
        | IStreamConversation<TInput>[]
        | ((prev: IStreamConversation<TInput>[]) => IStreamConversation<TInput>[]),
    ) => {
      const prev = stagedConversationsRef.current || conversationsRef.current;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      stagedConversationsRef.current = next;
      schedulePublish();
    },
    [schedulePublish],
  );

  /**
   * 根据 messageId 精准更新单条会话，未找到时保持原列表不变。
   */
  const updateConversationById = useCallback(
    (messageId: string, updater: (target: IStreamConversation<TInput>) => IStreamConversation<TInput>) => {
      setConversationsInternal((prev) => {
        const next = [...prev];
        const index = next.findIndex((item) => item.messageId === messageId);
        if (index < 0) {
          return prev;
        }
        next[index] = updater(next[index]);
        return next;
      });
    },
    [setConversationsInternal],
  );

  /**
   * 立即将所有缓冲的文本增量 flush 到对应会话的 answer 中。
   * 会清除 pendingTextDeltaByMessageIdRef 与 textDeltaFlushTimerRef。
   */
  const flushTextDeltas = useCallback(() => {
    if (textDeltaFlushTimerRef.current !== null) {
      window.clearTimeout(textDeltaFlushTimerRef.current);
      textDeltaFlushTimerRef.current = null;
    }
    const entries = Object.entries(pendingTextDeltaByMessageIdRef.current);
    if (!entries.length) {
      return;
    }
    pendingTextDeltaByMessageIdRef.current = {};

    entries.forEach(([messageId, text]) => {
      if (!text) {
        return;
      }
      updateConversationById(messageId, (targetConversation) => {
        const { nextAnswer, nextState } = applyEventToAnswer(targetConversation.answer || [], {
          kind: 'TEXT_DELTA',
          text,
        });
        if (nextState) {
          setStreamingState(nextState);
        }
        return {
          ...targetConversation,
          answer: nextAnswer,
        };
      });
    });
  }, [updateConversationById]);

  /**
   * 调度文本增量 flush：若当前无定时器，则在 TEXT_DELTA_FLUSH_MS 后执行 flush。
   * 与长度阈值 flush 配合使用，兼顾实时性与渲染性能。
   */
  const scheduleTextDeltaFlush = useCallback(() => {
    if (textDeltaFlushTimerRef.current !== null) {
      return;
    }
    textDeltaFlushTimerRef.current = window.setTimeout(() => {
      textDeltaFlushTimerRef.current = null;
      flushTextDeltas();
    }, TEXT_DELTA_FLUSH_MS);
  }, [flushTextDeltas]);

  // 组件卸载时清理 RAF 与定时器，防止内存泄漏与对已卸载组件 setState
  useEffect(() => () => {
    if (publishRafIdRef.current !== null) {
      window.cancelAnimationFrame(publishRafIdRef.current);
      publishRafIdRef.current = null;
    }
    if (textDeltaFlushTimerRef.current !== null) {
      window.clearTimeout(textDeltaFlushTimerRef.current);
      textDeltaFlushTimerRef.current = null;
    }
  }, []);

  /**
   * 取消当前流式请求：
   * 1. 先 flush 所有未提交的文本增量；
   * 2. 立即发布 staged 数据；
   * 3. abort 网络请求；
   * 4. 若未完成则将状态置为 rejected。
   */
  const cancelStreaming = useCallback(() => {
    flushTextDeltas();
    publishNow();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreamingState((prev) => (prev === 'complete' ? prev : 'rejected'));
  }, [flushTextDeltas, publishNow]);

  /**
   * 外部使用的 setConversations，语义与 React setState 一致（直接赋值或函数式更新）。
   */
  const setConversations = useCallback(
    (
      updater:
        | IStreamConversation<TInput>[]
        | ((prev: IStreamConversation<TInput>[]) => IStreamConversation<TInput>[]),
    ) => {
      setConversationsInternal(updater);
    },
    [setConversationsInternal],
  );

  /**
   * 清空全部会话与状态，同时取消进行中的流式请求。
   */
  const clearConversations = useCallback(() => {
    cancelStreaming();
    if (textDeltaFlushTimerRef.current !== null) {
      window.clearTimeout(textDeltaFlushTimerRef.current);
      textDeltaFlushTimerRef.current = null;
    }
    pendingTextDeltaByMessageIdRef.current = {};
    stagedConversationsRef.current = [];
    publishNow();
    setStreamingState('idle');
  }, [cancelStreaming, publishNow]);

  /**
   * 发送消息并启动 SSE 流式会话。
   * 流程：
   * 1. 若存在进行中的请求，先 abort（覆盖发送场景）；
   * 2. 生成 messageId，构建初始会话并立即发布到 UI（pending 状态）；
   * 3. 通过 fetchEventSource 建立 SSE 连接；
   * 4. 接收 onmessage 后，TEXT_DELTA 进入缓冲队列（按时间/长度阈值 flush），
   *    其他事件（APPEND_BLOCK / DONE / ERROR / SET_STATE）立即处理；
   * 5. onclose 时 flush 文本、置为 complete；
   * 6. onerror / catch 时注入错误块并置为 rejected；
   * 7. finally 中清理 abortController 引用并确保 staged 发布。
   */
  const sendMessage = useCallback(
    async ({ input }: ISendInput<TInput>) => {
      // 覆盖发送：终止旧请求（旧请求不会触发后续 onmessage）
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const messageId = createMessageId?.() || uuidv4();
      onBeforeSend?.({ messageId, input });

      const initialAnswer = getInitialAnswer?.(input) || [];
      const newConversation: IStreamConversation<TInput> = {
        messageId,
        input,
        answer: initialAnswer,
        role: 'assistant',
        createdAt: Date.now(),
      };

      // 将会话加入列表并立即发布（用户需立即看到发送的消息）
      setConversationsInternal((prev) => [...prev, newConversation]);
      publishNow();
      setStreamingState('pending');

      const request = await Promise.resolve(buildRequest(input, {
        signal: abortController.signal,
        messageId,
      }));

      try {
        await fetchEventSource(request.url, {
          method: request.method || 'POST',
          headers: request.headers,
          body: request.body,
          signal: abortController.signal,
          openWhenHidden: true,
          onopen: async (response) => {
            if (!response.ok) {
              throw new Error(`SSE connection failed: ${response.status}`);
            }
            onOpen?.({ messageId });
          },
          onmessage: (rawChunk) => {
            // 归一化原始 chunk，透传给外部回调
            const normalizedChunk: IRawStreamChunk = {
              event: rawChunk.event,
              data: rawChunk.data,
              id: rawChunk.id,
              retry: rawChunk.retry,
            };
            onSuccessChunk?.({ messageId, rawChunk: normalizedChunk });

            // 解析为内部事件列表（协议无关，由调用方 parseChunk 实现）
            const events = parseChunk(normalizedChunk, { messageId, input });
            if (!events.length) {
              return;
            }

            // ---- 分流处理：TEXT_DELTA 进缓冲，其他事件立即处理 ----
            let mergedTextDelta = '';
            const immediateEvents: TStreamEvent[] = [];
            for (const event of events) {
              if (event.kind === 'TEXT_DELTA') {
                mergedTextDelta += String(event.text || '');
              } else {
                immediateEvents.push(event);
              }
            }

            // 文本增量缓冲：按 messageId 聚合，超阈值立即 flush，否则启动定时器
            if (mergedTextDelta) {
              pendingTextDeltaByMessageIdRef.current[messageId] = `${pendingTextDeltaByMessageIdRef.current[messageId] || ''}${mergedTextDelta}`;
              if (pendingTextDeltaByMessageIdRef.current[messageId].length >= TEXT_DELTA_FLUSH_CHAR_THRESHOLD) {
                flushTextDeltas();
              } else {
                scheduleTextDeltaFlush();
              }
            }

            // 无即时事件则无需后续处理
            if (!immediateEvents.length) {
              return;
            }

            // 处理即时事件前，先确保文本增量已 flush（保证事件顺序）
            flushTextDeltas();
            publishNow();

            // 按顺序将即时事件应用到对应会话
            updateConversationById(messageId, (targetConversation) => {
              let mergedAnswer = targetConversation.answer || [];
              let derivedState: Exclude<TStreamingState, 'idle'> | undefined;

              for (const event of immediateEvents) {
                const { nextAnswer, nextState } = applyEventToAnswer(mergedAnswer, event);
                mergedAnswer = nextAnswer;
                if (nextState) {
                  derivedState = nextState;
                }
              }

              if (derivedState) {
                setStreamingState(derivedState);
              }

              return {
                ...targetConversation,
                answer: mergedAnswer,
              };
            });
          },
          onclose: () => {
            // 连接正常关闭：flush 剩余文本、发布数据、置为 complete
            flushTextDeltas();
            publishNow();
            setStreamingState('complete');
            onComplete?.({ messageId });
          },
          onerror: (error) => {
            // SSE 库错误：flush 后抛出让 catch 块统一处理
            flushTextDeltas();
            publishNow();
            setStreamingState('rejected');
            onError?.({ messageId, error });
            throw error;
          },
        });
      } catch (error) {
        // 网络异常 / onopen 失败 / onerror 抛出的错误统一在此处理
        flushTextDeltas();
        publishNow();
        updateConversationById(messageId, (targetConversation) => {
          const { nextAnswer } = applyEventToAnswer(targetConversation.answer || [], {
            kind: 'ERROR',
            error,
          });

          return {
            ...targetConversation,
            answer: nextAnswer,
          };
        });
        publishNow();
        setStreamingState('rejected');
      } finally {
        // 确保最终 staged 数据一定发布；若当前 controller 仍是本次创建的，则清空引用
        publishNow();
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      buildRequest,
      createMessageId,
      getInitialAnswer,
      onBeforeSend,
      onComplete,
      onError,
      onOpen,
      onSuccessChunk,
      parseChunk,
      publishNow,
      flushTextDeltas,
      scheduleTextDeltaFlush,
      setConversationsInternal,
      updateConversationById,
    ],
  );

  // 返回值对象做 memo：保证引用稳定，避免调用方把整个返回对象作为 props/依赖时
  // 因“每次 render 都新建对象”引发不必要的子组件重渲染或 useEffect 重复触发。
  // 当 conversations / streamingState 或这些 action 引用变化时，返回值才会更新。
  return useMemo(
    () => ({
      conversations,
      streamingState,
      sendMessage,
      cancelStreaming,
      clearConversations,
      setConversations,
    }),
    [
      cancelStreaming,
      clearConversations,
      conversations,
      sendMessage,
      setConversations,
      streamingState,
    ],
  );
};

